import * as React from "react"
import * as Brain from "models/brain";
import { AppContext } from "AppContext";
import { ActionTypes } from "reducer/actions";
import { Button, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import { Link } from "react-scroll";
import { FilterContext } from "models/core/filter";
import { DashChip } from "components/common/DashChips";
import ReportApi from "api/reportApi";
import * as fileHelper from "helpers/fileHelper";
import { Download, Downloading } from "@mui/icons-material";
import { BrainIndicatorDefinitionsUrl } from "config/appUrlConfig";
import { getStateIndicatorReferences } from "api/dashboardApi";
import Loader from "pages/shared/pageloader";

enum BrainIndicatorAction {
	UserClickedDownloadIndicator,
	DownloadIndicatorSuccess,
	DownloadIndicatorFail
}

type Action =
	{ type: BrainIndicatorAction.UserClickedDownloadIndicator, payload: number }
	| { type: BrainIndicatorAction.DownloadIndicatorSuccess }
	| { type: BrainIndicatorAction.DownloadIndicatorFail }


interface State {
	indicator: number
}

const initialState: State = {
	indicator: 0
}



const reducer = (state: State, action: Action): State => {
	const { type } = action;

	switch (type) {
		case BrainIndicatorAction.UserClickedDownloadIndicator:
			return { ...state, ...{ indicator: action.payload } };

		case BrainIndicatorAction.DownloadIndicatorSuccess:
		case BrainIndicatorAction.DownloadIndicatorFail:
			return { ...state, ...{ indicator: 0 } };

		default:
			return state;
	}
}

let stateWideData: {
	title?: string,
	patients?: string,
	histDiag?: string,
	craniotomy30DaysDiagnosis?: string,
	craniotomyForHistDiagGlioAnaOligoAnaAstro?: string,
	craniotomyForepend?: string,
	inHospitalMortality?: string,
	mortality30Days?: string,
	mortality90Days?: string,
	oneYearSurvival?: string,
	twoYearSurvival?: string,
	rt45To54LowGrade?: string,
	rt54To60Under70Grade4?: string,
	rtUnder70HighGrade?: string,
	rt70PlusHighGrade?: string,
	rtAnaOligo?: string,
	rtAnaAstro?: string,
	rt49DaysHistDiagHighGrade?: string
} = {};


export function BrainDashboard() {
	const [localState, localDispatch] = React.useReducer(reducer, initialState)
	const { state, dispatch } = React.useContext(AppContext);


	const [showFilterChips, setFilterChips] = React.useState(true);

	const addFilter: Brain.AddFilterFunc = async (key: keyof typeof Brain.DashboardFilterKeys, value: string | string[], context?: FilterContext, labelOverride?: string) => {
		const filteredDashboard = await Brain.computeChartData(Brain.addFilter(state.page.dashboard as Brain.IBrainDashboard)(key, value, context, labelOverride)); // state = what the other filter function returns 
		dispatch({ type: ActionTypes.UserClickedOnChart, payload: filteredDashboard });
	}

	const removeFilter: Brain.RemoveFilterFunc = async (key: keyof typeof Brain.DashboardFilterKeys, value?: string) => {
		const filteredDashboard = await Brain.computeChartData(Brain.removeFilter(state.page.dashboard as Brain.IBrainDashboard, key, value));
		dispatch({ type: ActionTypes.UserClickedOnChart, payload: filteredDashboard });
	}

	React.useEffect(() => {
		dispatch({ type: ActionTypes.UserWantsToLoadMdtDashboard });

		const fetchStateReferenceData = async () => {
			stateWideData = await getStateIndicatorReferences("brain");
		}

		const loadDashboard = async () => {
			const value = await Brain.loadBrainDashboard(state.page.dashboard as Brain.IBrainDashboard);
			dispatch({ type: ActionTypes.UserLoadedMdtDashboard, payload: value })
		}

		Promise.all([fetchStateReferenceData(), loadDashboard()]);

		return () => {
			Brain.closeBrainDashboard();
		}
	}, []);

	React.useEffect(() => {
		if (localState.indicator == 0)
			return;

		let ids = brainChartData.level1.filteredReferralIdCsv;
		let alldata = false;


		const getBrainIndicatorPatientList = async () => {
			try {
				const data = await ReportApi.getBrainIndicatorPatientList(ids, alldata, localState.indicator);
				if (data.errorMessage) {
					console.error(data.errorMessage);
					localDispatch({ type: BrainIndicatorAction.DownloadIndicatorFail })
				}
				localDispatch({ type: BrainIndicatorAction.DownloadIndicatorSuccess })
				return fileHelper.showFile(data.bytes, data.filename, fileHelper.fileTypeToMimeType(data.filetype), "exportPatientsElem")
			} catch {
				localDispatch({ type: BrainIndicatorAction.DownloadIndicatorFail })
			}
		}

		getBrainIndicatorPatientList();


	}, [localState.indicator]);


	if (state.page.loading || !state.page.dashboard.chartData)
		return <Loader />

	//Create function to show/hide filter chips
	const toggleChips = () => {
		if (showFilterChips) {
			setFilterChips(false);
		} else {
			setFilterChips(true);
		}
	};

	let activeFiltersArray = [];
	let filterArray = [];
	let chips = [];

	for (const key in state.page.dashboard.filters) {
		if (Object.prototype.hasOwnProperty.call(state.page.dashboard.filters, key)) {
			activeFiltersArray.push({ "key": key, "values": state.page.dashboard.filters[key].value, "parts": state.page.dashboard.filters[key].parts });
		}
	}

	for (const key in Brain.DashboardFilterKeys) {
		if (Object.prototype.hasOwnProperty.call(Brain.DashboardFilterKeys, key)) {
			filterArray.push({ "key": key, "label": Brain.DashboardFilterKeys[key].label });
		}
	}

	activeFiltersArray.forEach(a => {
		a.values.forEach(b => {
			chips.push(<DashChip key={a.key + b}
				removeFilterFn={removeFilter}
				filterName={filterArray.find(id => id.key == a.key).label}
				filterValue={b}
				filterId={a.key}
				filterParent={(a.parts.length > 1 ? a.parts[0] : null)}
			/>
			)
		})
	})

	const brainChartData = state.page.dashboard.chartData as Brain.IChartData;


	const downloadBtn = (extractTypeId: number) => {
		const { indicator } = localState;

		if (indicator == extractTypeId)
			return <Downloading />

		return (
			<Download style={{ cursor: "pointer" }} onClick={() =>
				localDispatch({ type: BrainIndicatorAction.UserClickedDownloadIndicator, payload: extractTypeId })} />
		)
	}

	const ecog = {
		numerator: 0,
		denominator: 0,
		percentage: 0
	}

	const stage = {
		numerator: 0,
		denominator: 0,
		percentage: 0
	}

	if (brainChartData.level1.totalDiagnoses > 0) {
		ecog.numerator = brainChartData.level1.totalDiagnoses - (brainChartData.level2.ecogGroup.values[brainChartData.level2.ecogGroup.labels.indexOf("Unk")] || 0);
		ecog.denominator = brainChartData.level1.totalDiagnoses
		ecog.percentage = ecog.numerator / ecog.denominator * 100

		stage.numerator = brainChartData.level1.totalDiagnoses - (brainChartData.level2.tnmStage.values[brainChartData.level2.tnmStage.labels.indexOf("Unk")] || 0);
		stage.denominator = brainChartData.level1.totalDiagnoses;
		stage.percentage = stage.numerator / stage.denominator * 100
	}


	const dashboard = <>
		<div style={{
			position: "sticky",
			top: "64px",
			left: "10px",
			zIndex: 2,
			backgroundColor: "white"
		}}>


			{(showFilterChips ? <div style={{ marginLeft: "15px", marginBottom: "15px", marginTop: "10px", width: "80%" }}>
				{chips}
			</div> : null)}
		</div>



		<div style={{
			maxWidth: 1000,
			margin: "0 auto"
		}}>

			<div style={{
				textAlign: "right",
				paddingBottom: "10px",
				paddingTop: "10px",
				position: "sticky",
				zIndex: 1,
				top: "60px",
				backgroundColor: "white"
			}}>
				<Button onClick={() => localDispatch({ type: BrainIndicatorAction.UserClickedDownloadIndicator, payload: 1 })}
					style={{
						backgroundColor: (brainChartData.level1.totalDiagnoses > 0 ? ("#1e88e5") : ("#d3d3d3")),
						color: "white",
						alignItems: "right"
					}}>
					Download Patient Details
				</Button>
			</div>
			<TableContainer component={Paper}>
				<Table size="small">
					<TableHead>
						<TableRow>
							<TableCell>Indicator</TableCell>
							<TableCell align="center">My MDT(s)*</TableCell>
							<TableCell align="center">{stateWideData.title}^</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						<TableRow>
							<TableCell>Primary brain cancer diagnoses^</TableCell>
							<TableCell align="center">{`${brainChartData.level1.totalDiagnoses}`}</TableCell>
							<TableCell align="center">{stateWideData.patients}</TableCell>
						</TableRow>

						<TableRow>
							<TableCell>Percentage of patients with ECOG</TableCell>
							<TableCell align="center">{`${ecog.percentage.toFixed(0)}%`} <br /> {`(${ecog.numerator}/${ecog.denominator})`}</TableCell>
							<TableCell align="center">-</TableCell>
						</TableRow>

						<TableRow>
							<TableCell colSpan={4}>
								<b>Diagnosis</b>
							</TableCell>
						</TableRow>

						<TableRow>
							<TableCell>Primary brain cancer was histologically diagnosed?</TableCell>
							<TableCell align="center">{`${brainChartData.level1.histDiag.percentage.toFixed(0)}%`} <br /> {`(${brainChartData.level1.histDiag.numerator}/${brainChartData.level1.histDiag.denominator})`}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData.histDiag}</TableCell>
						</TableRow>

						<TableRow>
							<TableCell colSpan={4}>
								<b>Surgical</b>
							</TableCell>
						</TableRow>
						<TableRow>
							<TableCell>Received craniotomy as first treatment within 30 days of diagnosis?</TableCell>
							<TableCell align="center">{`${brainChartData.level1.craniotomy30DaysDiagnosis.percentage.toFixed(0)}%`} <br /> {`(${brainChartData.level1.craniotomy30DaysDiagnosis.numerator}/${brainChartData.level1.craniotomy30DaysDiagnosis.denominator})`}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData.craniotomy30DaysDiagnosis}</TableCell>

						</TableRow>
						<TableRow>
							<TableCell>Craniotomy for histologically diagnosed glioblastoma, anaplastic oligodendroglioma or anaplastic astrocytoma </TableCell>
							<TableCell align="center">{`${brainChartData.level1.craniotomyForHistDiagGlioAnaOligoAnaAstro.percentage.toFixed(0)}%`} <br /> {`(${brainChartData.level1.craniotomyForHistDiagGlioAnaOligoAnaAstro.numerator}/${brainChartData.level1.craniotomyForHistDiagGlioAnaOligoAnaAstro.denominator})`}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData.craniotomyForHistDiagGlioAnaOligoAnaAstro}</TableCell>

						</TableRow>
						<TableRow>
							<TableCell>Craniotomy for histologically diagnosed ependymoma</TableCell>
							<TableCell align="center">{`${brainChartData.level1.craniotomyForepend.percentage.toFixed(0)}%`} <br /> {`(${brainChartData.level1.craniotomyForepend.numerator}/${brainChartData.level1.craniotomyForepend.denominator})`}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData.craniotomyForepend}</TableCell>
						</TableRow>

						<TableRow>
							<TableCell>Died during last craniotomy admission</TableCell>
							<TableCell align="center">{`${brainChartData.level1.inHospitalMortality.percentage.toFixed(1)}%`} <br /> {`(${brainChartData.level1.inHospitalMortality.numerator}/${brainChartData.level1.inHospitalMortality.denominator})`}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData.inHospitalMortality}</TableCell>
						</TableRow>

						<TableRow>
							<TableCell>Died within 30 days of craniotomy</TableCell>
							<TableCell align="center">{`${brainChartData.level1.mortality30Days.percentage.toFixed(1)}%`} <br /> {`(${brainChartData.level1.mortality30Days.numerator}/${brainChartData.level1.mortality30Days.denominator})`}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData.mortality30Days}</TableCell>
						</TableRow>

						<TableRow>
							<TableCell>Died within 90 days of craniotomy</TableCell>
							<TableCell align="center">{`${brainChartData.level1.mortality90Days.percentage.toFixed(0)}%`} <br /> {`(${brainChartData.level1.mortality90Days.numerator}/${brainChartData.level1.mortality90Days.denominator})`}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData.mortality90Days}</TableCell>
						</TableRow>
						<TableRow>
							<TableCell>1 yrs survival after 1st craniotomy^</TableCell>
							<TableCell align="center">{`${brainChartData.level1.oneYearSurvival.percentage.toFixed(0)}%`} <br /> {`(${brainChartData.level1.oneYearSurvival.numerator}/${brainChartData.level1.oneYearSurvival.denominator})`}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData.oneYearSurvival}</TableCell>
						</TableRow>
						<TableRow>
							<TableCell>2 yrs survival after 1st craniotomy^</TableCell>
							<TableCell align="center">{`${brainChartData.level1.twoYearSurvival.percentage.toFixed(0)}%`} <br /> {`(${brainChartData.level1.twoYearSurvival.numerator}/${brainChartData.level1.twoYearSurvival.denominator})`}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData.twoYearSurvival}</TableCell>
						</TableRow>

						<TableRow>
							<TableCell colSpan={6}> <i>^Patients with less than one and two years of data from their first surgery are excluded from the one and two year survival indicators respectively.</i></TableCell>
						</TableRow>

						<TableRow>
							<TableCell colSpan={4}>
								<b>Radiation Therapy</b>
							</TableCell>
						</TableRow>

						<TableRow>
							<TableCell>Radiation therapy dosimetry of 45-54Gy for low grade glioma </TableCell>
							<TableCell align="center">{`${brainChartData.level1.rt45To54LowGrade.percentage.toFixed(0)}%`} <br /> {`(${brainChartData.level1.rt45To54LowGrade.numerator}/${brainChartData.level1.rt45To54LowGrade.denominator})`}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData.rt45To54LowGrade}</TableCell>
						</TableRow>

						<TableRow>
							<TableCell>Radiation therapy dosimetry of 54-60Gy for people aged under 70 with grade 4 gliomas </TableCell>
							<TableCell align="center">{`${brainChartData.level1.rt54To60Under70Grade4.percentage.toFixed(0)}%`} <br /> {`(${brainChartData.level1.rt54To60Under70Grade4.numerator}/${brainChartData.level1.rt54To60Under70Grade4.denominator})`}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData.rt54To60Under70Grade4}</TableCell>
						</TableRow>

						<TableRow>
							<TableCell>Received radiation therapy for people aged under 70 with high grade glioma</TableCell>
							<TableCell align="center">{`${brainChartData.level1.rtUnder70HighGrade.percentage.toFixed(0)}%`} <br /> {`(${brainChartData.level1.rtUnder70HighGrade.numerator}/${brainChartData.level1.rtUnder70HighGrade.denominator})`}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData.rtUnder70HighGrade}</TableCell>
						</TableRow>

						<TableRow>
							<TableCell>Radiation therapy for people aged 70 and older with high grade glioma </TableCell>
							<TableCell align="center">{`${brainChartData.level1.rt70PlusHighGrade.percentage.toFixed(0)}%`} <br /> {`(${brainChartData.level1.rt70PlusHighGrade.numerator}/${brainChartData.level1.rt70PlusHighGrade.denominator})`}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData.rt70PlusHighGrade}</TableCell>
						</TableRow>

						<TableRow>
							<TableCell>Radiation therapy for anaplastic oligodendroglioma</TableCell>
							<TableCell align="center">{`${brainChartData.level1.rtAnaOligo.percentage.toFixed(0)}%`} <br /> {`(${brainChartData.level1.rtAnaOligo.numerator}/${brainChartData.level1.rtAnaOligo.denominator})`}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData.rtAnaOligo}</TableCell>
						</TableRow>
						<TableRow>
							<TableCell>Radiation therapy for anaplastic astrocytoma </TableCell>
							<TableCell align="center">{`${brainChartData.level1.rtAnaAstro.percentage.toFixed(0)}%`} <br /> {`(${brainChartData.level1.rtAnaAstro.numerator}/${brainChartData.level1.rtAnaAstro.denominator})`}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData.rtAnaAstro}</TableCell>
						</TableRow>

						<TableRow>
							<TableCell>Radiation therapy within 49 days of diagnosis for people with histologically diagnosed high grade glioma </TableCell>
							<TableCell align="center">{`${brainChartData.level1.rt49DaysHistDiagHighGrade.percentage.toFixed(0)}%`} <br /> {`(${brainChartData.level1.rt49DaysHistDiagHighGrade.numerator}/${brainChartData.level1.rt49DaysHistDiagHighGrade.denominator})`}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData.rt49DaysHistDiagHighGrade}</TableCell>
						</TableRow>

					</TableBody>
				</Table>
			</TableContainer>

			<div style={{ fontSize: "small", textAlign: "left" }}>
				<p><i>*Data based on facility of MDT</i> </p>
			</div>

			<div style={{ fontSize: "small", textAlign: "left" }}>
				<p><i>^The comparison data in the Qld report is based on a person's incidence case of the cancer. The MDT data is all diagnoses (with a diagnosis date) presented to MDT using the Qool application</i> </p>
			</div>

			<div style={{ textAlign: "left" }}>
				<p>This report is based on the <a href={BrainIndicatorDefinitionsUrl} target="_blank">Queensland Brain Cancer Quality Index 2011-2020.</a> Indicator definitions and glossary can be found here also</p>
			</div>

		</div>

		<a id="exportPatientsElem" style={{ display: "none" }}></a>

	</>

	return (
		dashboard
	)


} 